import type * as type from "@vTypes";
import CustomModal from "@/components/modal/CustomModal";
import { ArrowRight } from "lucide-react";
import LoadingSpiner from "@/components/ui/LoadingSpiner";
import { cn } from "@/utils/cn";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  showUnreadOnly: boolean;
  setShowUnreadOnly: (value: boolean) => void;
  unreadCount: number;
  isMarkingAsRead: boolean;
  onMarkAllAsRead: () => void;
  isModalLoading: boolean;
  filteredAlarmList: type.AlarmItem[];
  parseAlarmLink: (item: type.AlarmItem) => string | null;
  onAlarmClick: (item: type.AlarmItem) => void;
};

export default function HeaderAlarmModal({
  isOpen,
  onClose,
  showUnreadOnly,
  setShowUnreadOnly,
  unreadCount,
  isMarkingAsRead,
  onMarkAllAsRead,
  isModalLoading,
  filteredAlarmList,
  parseAlarmLink,
  onAlarmClick,
}: Props) {
  if (!isOpen) return null;

  return (
    <CustomModal.Panel
      onClose={onClose}
      dismissOnOverlay
      className={cn(
        "w-[min(480px,calc(100vw-48px))] max-w-[min(520px,calc(100vw-48px))] bg-white",
        "border border-[#D6D6DE] shadow-lg",
        "[&>div]:!p-0 [&>div]:overflow-hidden"
      )}
    >
      <div className="flex max-h-[min(720px,calc(100vh-180px))] min-h-[300px] min-w-0 flex-col">
        <div className="flex items-center justify-between px-0 sticky top-0 bg-white z-10">
          <h3 className="font-semibold text-lg">전체 알림</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-xl"
          >
            ✕
          </button>
        </div>

        <div className="px-0 py-4 border-b flex gap-1 items-center">
          <button
            type="button"
            onClick={() => setShowUnreadOnly(!showUnreadOnly)}
            className={`px-2 py-1.5 rounded-lg text-sm! font-medium transition-colors border ${
              showUnreadOnly
                ? "text-sig border-sig bg-white"
                : "text-gray-700 border-gray-300 bg-white hover:border-gray-400"
            }`}
          >
            안읽은 알람보기 {unreadCount > 0 && `(${unreadCount})`}
          </button>
          <button
            type="button"
            onClick={onMarkAllAsRead}
            disabled={isMarkingAsRead || unreadCount === 0}
            className={`px-2 py-1.5 rounded-lg text-sm! font-medium transition-colors ml-auto border ${
              isMarkingAsRead || unreadCount === 0
                ? "bg-white text-zinc-400 border-zinc-300 cursor-not-allowed"
                : "bg-white text-gray-700 border-gray-300 hover:border-gray-400"
            }`}
          >
            {isMarkingAsRead ? "처리 중..." : "전체 읽음 처리"}
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto scrollbar-float pb-10">
          {isModalLoading ? (
            <div className="py-8 flex items-center justify-center pt-[110px]">
              <LoadingSpiner />
            </div>
          ) : filteredAlarmList.length === 0 ? (
            <div className="py-8 text-center text-gray-500 pt-[110px]">
              {showUnreadOnly
                ? "읽지 않은 알림이 없습니다."
                : "알림이 없습니다."}
            </div>
          ) : (
            <div>
              {filteredAlarmList.map((item, index) => {
                const link = parseAlarmLink(item);
                const isClickable = link !== null;
                const rowActs =
                  isClickable || (item.ReadYN === "N" && item.fk_idx);
                return (
                  <div
                    key={index}
                    onClick={() => rowActs && onAlarmClick(item)}
                    className={`py-3 px-0 transition-colors border-b border-gray-200 ${
                      item.ReadYN === "N" ? "bg-blue-50/30" : ""
                    } ${rowActs ? "hover:bg-gray-50 cursor-pointer" : ""}`}
                  >
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1">
                          <p
                            className={`text-sm ${
                              item.ReadYN === "N"
                                ? "font-semibold text-sig"
                                : "font-medium text-gray-900"
                            }`}
                          >
                            {item.Message}
                          </p>
                          {isClickable && (
                            <ArrowRight
                              className={`w-4 h-4 flex-shrink-0 ${
                                item.ReadYN === "N"
                                  ? "text-sig"
                                  : "text-gray-400"
                              }`}
                            />
                          )}
                        </div>
                      </div>
                      <div className="flex items-center flex-shrink-0">
                        <span className="text-xs text-gray-500 whitespace-nowrap">
                          {item.regdate.replaceAll("-", ".")}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </CustomModal.Panel>
  );
}
